// src/hooks/useMarketData.js
// 가격 시세: /api/quote-stream — 서버에서 병렬 호출, NDJSON 스트림으로 도착 즉시 반영
// 미국 PE/배당: Finnhub 직접 호출 (가격 표시 후 백그라운드 진행)
// 환율: open.er-api.com

import { useState, useCallback } from "react";
import { findByName } from "../lib/krStocks";

// KR 종목인데 티커 자리에 숫자 코드가 없으면(=종목명이 티커에 들어감) 종목명으로 코드 해석.
// 시세는 해석된 코드로 조회하되, 결과는 원래 ticker 키로 되돌려 매핑(시트 데이터는 안 바꿈).
function resolveFetch(h) {
  const market = String(h.market || "").toUpperCase();
  const isKR = market === "KR" || market === "KS" || market === "KQ";
  const t = String(h.ticker ?? "");
  if (isKR && !t.replace(/\D/g, "")) {
    const found = findByName(h.name);
    if (found) return { ticker: found.c, market: found.m === "KR" ? "KR" : found.m };
  }
  return { ticker: t, market: h.market };
}

function guessCurrency(market) {
  if (market === "JP") return "JPY";
  if (market === "HK") return "HKD";
  if (market === "TW") return "TWD";
  if (market === "KR" || market === "KS" || market === "KQ") return "KRW";
  return "USD";
}

function getToUsdRate(currency, rates) {
  if (currency === "JPY") return 1 / (rates.JPY || 149);
  if (currency === "HKD") return 1 / (rates.HKD || 7.8);
  if (currency === "TWD") return 1 / (rates.TWD || 31.5);
  if (currency === "KRW") return 1 / (rates.KRW || 1380);
  return 1;
}

async function fetchExchangeRates() {
  try {
    const res = await fetch("https://open.er-api.com/v6/latest/USD");
    const data = await res.json();
    const r = data.rates || {};
    return {
      USD: 1,
      KRW: parseFloat(r.KRW) || 1380,
      JPY: parseFloat(r.JPY) || 149,
      TWD: parseFloat(r.TWD) || 31.5,
      HKD: parseFloat(r.HKD) || 7.8,
    };
  } catch {
    return { USD: 1, KRW: 1380, JPY: 149, TWD: 31.5, HKD: 7.8 };
  }
}

function buildEntry(quote, market, rates) {
  // 활성 세션이 Pre/Post(또는 closed gap)면 가장 최근 확장가로 평가 — 수익률에 자동 반영.
  // REGULAR 세션 중에는 regular price 유지. 그 외(PRE/PREPRE/POST/POSTPOST/CLOSED)에
  // 확장가가 있으면 그걸 사용 → after 종료~pre 시작 사이 gap에서도 마지막 after 가격 표시.
  const regPrice = quote.price;
  const prev = (regPrice != null && quote.change != null) ? regPrice - quote.change : null;
  let effPrice = regPrice;
  let effChange = quote.change;
  let effPct = quote.changePct;
  if (quote.marketState !== "REGULAR") {
    const extPrice = quote.preMarketPrice ?? quote.postMarketPrice;
    if (extPrice != null) {
      effPrice = extPrice;
      effChange = prev != null ? effPrice - prev : null;
      effPct = prev ? ((effPrice - prev) / prev) * 100 : quote.changePct;
    }
  }

  const currency = quote.currency || guessCurrency(market);
  const toUsd = getToUsdRate(currency, rates);
  const priceUSD = effPrice * toUsd;
  return {
    price: effPrice,
    priceUSD,
    priceKRW: priceUSD * rates.KRW,
    currency,
    change: effChange,
    changePct: effPct,
    pe: quote.pe ?? null,
    divYield: quote.divYield ?? null,
    name: null,
    source: quote.source,
    krMarket: quote.krMarket ?? null,   // "KS"=코스피 / "KQ"=코스닥 (네이버 응답 기준)
    marketState: quote.marketState ?? null,
    regularPrice: regPrice,
    regularChangePct: quote.changePct,
    preMarketPrice: quote.preMarketPrice ?? null,
    preMarketChangePercent: quote.preMarketChangePercent ?? null,
    postMarketPrice: quote.postMarketPrice ?? null,
    postMarketChangePercent: quote.postMarketChangePercent ?? null,
  };
}

// Cloudflare Workers 무료 플랜은 요청당 외부 서브리퀘스트 50개 제한.
// fetchOne이 종목당 최대 4회(Finnhub→Yahoo→네이버→.KQ) 폴백하므로 12종목이 최악 48회.
const CHUNK = 12;

// /api/quote-stream을 청크로 나눠 병렬 호출 — 각 호출이 독립된 서브리퀘스트 예산을 가진다.
async function streamQuotes(holdings, onItem) {
  const chunks = [];
  for (let i = 0; i < holdings.length; i += CHUNK) chunks.push(holdings.slice(i, i + CHUNK));
  const results = await Promise.allSettled(chunks.map((c) => streamChunk(c, onItem)));
  const failed = results.filter((r) => r.status === "rejected");
  // 전 청크 실패만 에러로 승격(일부 실패는 해당 종목이 미로딩으로 남고 재시도 패스가 처리)
  if (failed.length === chunks.length && chunks.length) throw failed[0].reason;
}

async function streamChunk(holdings, onItem) {
  // 이름→코드 해석 + 조회코드↔원래티커 역매핑
  const back = new Map();
  const symbols = holdings.map(h => {
    const r = resolveFetch(h);
    back.set(String(r.ticker), String(h.ticker));
    return `${r.ticker}:${r.market}`;
  }).join(",");
  const res = await fetch(`/api/quote-stream?symbols=${encodeURIComponent(symbols)}`);
  if (!res.ok || !res.body) throw new Error(`stream failed: ${res.status}`);

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  const emit = (line) => {
    let it; try { it = JSON.parse(line); } catch { return; }
    const orig = back.get(String(it.ticker));   // 조회코드 → 원래 티커로 되돌림
    onItem(orig ? { ...it, ticker: orig } : it);
  };

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let nl;
    while ((nl = buffer.indexOf("\n")) >= 0) {
      const line = buffer.slice(0, nl).trim();
      buffer = buffer.slice(nl + 1);
      if (line) emit(line);
    }
  }
  const tail = buffer.trim();
  if (tail) emit(tail);
}

export function useMarketData() {
  const [marketData,    setMarketData]    = useState({});
  const [exchangeRates, setExchangeRates] = useState({ USD: 1, KRW: 1380, JPY: 149, TWD: 31.5, HKD: 7.8 });
  const [loading,       setLoading]       = useState(false);
  const [lastUpdated,   setLastUpdated]   = useState(null);
  const [error,         setError]         = useState(null);

  const refresh = useCallback(async (holdings) => {
    if (!holdings || holdings.length === 0) return;
    setLoading(true);
    setError(null);

    try {
      const rates = await fetchExchangeRates();
      setExchangeRates(rates);

      // 가격 스트리밍 — 도착하는 종목부터 즉시 화면 반영
      const loaded = {};
      await streamQuotes(holdings, ({ ticker, market, data }) => {
        if (!data) { console.warn(`no price for ${ticker}`); return; }
        const entry = buildEntry(data, market, rates);
        loaded[ticker] = entry;
        setMarketData(prev => ({ ...prev, [ticker]: entry }));
      });

      setLastUpdated(new Date());
    } catch (err) {
      setError("시세 데이터를 가져오는 데 실패했습니다.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // 실패한 종목만 재시도 — 같은 스트림 엔드포인트 재사용
  const retryFailed = useCallback(async (failedHoldings) => {
    if (!failedHoldings || failedHoldings.length === 0) return;
    try {
      const rates = exchangeRates;
      const updates = {};
      await streamQuotes(failedHoldings, ({ ticker, market, data }) => {
        if (!data) return;
        updates[ticker] = buildEntry(data, market, rates);
      });
      if (Object.keys(updates).length > 0) {
        setMarketData(prev => ({ ...prev, ...updates }));
        setLastUpdated(new Date());
        console.log(`[Retry] ${Object.keys(updates).length}개 추가 로드:`, Object.keys(updates));
      }
    } catch (err) {
      console.error("Retry failed:", err);
    }
  }, [exchangeRates]);

  return { marketData, exchangeRates, loading, lastUpdated, error, refresh, retryFailed };
}
